import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, BarChart3, TrendingUp, ChevronRight, Star } from 'lucide-react';
import { SiteFooter } from '@/components/SiteFooter';
import { ShareBar } from '@/components/ShareButtons';

import { Logo } from '@/components/Logo';

export default function Landing() {
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const handleStart = () => {
    navigate('/assess');
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]" data-testid="main-nav">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Logo />
          <div className="flex items-center gap-4">
            {user ? (
              <Button
                data-testid="dashboard-nav-btn"
                onClick={() => navigate('/dashboard')}
                className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-6 py-2 text-sm"
              >
                Dashboard
              </Button>
            ) : (
              <Button
                data-testid="login-nav-btn"
                onClick={login}
                variant="ghost"
                className="text-[#1D1D1F] text-sm"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FBFBFD] via-[#F5F5F7] to-[#EBEBF0]" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#E5E5EA]/30 blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#E5E5EA]/20 blur-3xl translate-y-1/3 -translate-x-1/4" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 pt-20 pb-16 md:pt-28 md:pb-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.1] font-bold text-[#1D1D1F] mb-4 animate-[fadeIn_0.8s_ease]">
              Most food businesses don't fail
              <br />
              because of bad food.
            </h1>
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.1] font-bold text-[#1D1D1F]/30 mb-8 animate-[fadeIn_1s_ease]">
              They fail because of bad decisions.
            </h1>
            <p className="text-base md:text-lg text-[#1D1D1F]/60 leading-relaxed mb-10 max-w-lg font-medium animate-[fadeIn_1.2s_ease]">
              Find out if your idea, location, and pricing are setting you up for failure — before you invest lakhs.
            </p>
            <div className="animate-[fadeIn_1.4s_ease]">
              <Button
                data-testid="start-assessment-btn"
                onClick={handleStart}
                className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full py-5 px-10 text-base font-semibold transition-all duration-500 active:scale-95 shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.2)]"
                size="lg"
              >
                Check My Risk Now
                <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
              </Button>
              <p className="text-xs text-[#1D1D1F]/40 mt-4 font-medium">
                Free score in 2 minutes. Full risk report for just ₹499.
              </p>
            </div>
          </div>
          <p className="text-[10px] text-[#1D1D1F]/25 mt-12 font-semibold tracking-wide">
            No guesswork. No sugarcoating. Just reality.
          </p>
        </div>
      </section>

      {/* Food Industry Visual Strip */}
      <section className="py-0 overflow-hidden" data-testid="food-gallery">
        <div className="grid grid-cols-3 gap-1">
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1770277365467-47fce11d6bc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwbWluaW1hbHxlbnwwfHx8fDE3NzQ4NTc0OTB8MA&ixlib=rb-4.1.0&q=85"
              alt="Restaurant interior"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/5865434/pexels-photo-5865434.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Gourmet food plating"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1760326652193-bc25f0fe4a8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxjYWZlJTIwY29mZmVlJTIwc2hvcCUyMG1vZGVybiUyMG1pbmltYWx8ZW58MHx8fHwxNzc0ODU3NDkxfDA&ixlib=rb-4.1.0&q=85"
              alt="Modern cafe interior"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="bg-white py-10 md:py-14" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs md:text-sm tracking-widest uppercase font-bold text-[#1D1D1F]/50 mb-3">
            What You Get
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#1D1D1F] mb-8">
            A complete feasibility assessment.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: <BarChart3 className="h-6 w-6" strokeWidth={1.5} />,
                title: 'Financial Analysis',
                desc: 'Revenue, costs, profit margins, breakeven point, and runway — all calculated deterministically.',
                img: 'https://images.pexels.com/photos/12603336/pexels-photo-12603336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
              },
              {
                icon: <Shield className="h-6 w-6" strokeWidth={1.5} />,
                title: 'Risk Identification',
                desc: 'Automated flags for rent burden, staff costs, low margins, and operational risks.',
                img: 'https://images.pexels.com/photos/14110993/pexels-photo-14110993.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
              },
              {
                icon: <TrendingUp className="h-6 w-6" strokeWidth={1.5} />,
                title: 'Scenario Testing',
                desc: 'Modify rent, AOV, staff, or food cost. See how each change impacts your viability.',
                img: 'https://images.pexels.com/photos/35056936/pexels-photo-35056936.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl border border-[#E5E5EA] overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)]"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="text-[#1D1D1F] mb-3">{item.icon}</div>
                  <h3 className="text-xl md:text-2xl font-semibold text-[#1D1D1F] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#1D1D1F]/70 leading-relaxed font-medium">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F5F5F7] py-10 md:py-14" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <p className="text-xs md:text-sm tracking-widest uppercase font-bold text-[#1D1D1F]/50 mb-3">
              What Early Users Are Saying
            </p>
            <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1D1D1F]">
              People validating their food business ideas before investing.
            </h2>
          </div>

          <div className="flex gap-4 overflow-x-auto pb-4 -mx-6 px-6 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:overflow-visible snap-x snap-mandatory">
            {[
              { quote: "I was planning a small cafe and thought everything looked fine. The score showed my margins were too tight. Made me rethink a few things.", name: "Rahul S.", tag: "Cafe idea", initials: "RS" },
              { quote: "Honestly, I didn't realise how much rent was affecting the numbers. The report made it very clear.", name: "Priya M.", tag: "Restaurant concept", initials: "PM" },
              { quote: "I just wanted to test my idea quickly. The score gave a good starting point before I go deeper.", name: "Arjun K.", tag: "QSR concept", initials: "AK" },
              { quote: "I tried changing a few inputs and saw how the numbers changed. That helped me understand what actually matters.", name: "Sneha R.", tag: "Bakery plan", initials: "SR" },
              { quote: "I'm still exploring my concept, but this helped me see where the risks are.", name: "Karthik V.", tag: "Casual dining", initials: "KV" },
            ].map((t, i) => (
              <div key={i} className="min-w-[280px] md:min-w-0 snap-start bg-white rounded-2xl p-6 border border-[#E5E5EA] flex flex-col justify-between">
                <div>
                  <p className="text-sm text-[#1D1D1F]/70 leading-relaxed font-medium mb-4">"{t.quote}"</p>
                </div>
                <div className="flex items-center gap-3 mt-auto pt-3 border-t border-[#E5E5EA]">
                  <div className="w-8 h-8 rounded-full bg-[#1D1D1F] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#1D1D1F]">{t.name}</p>
                    <p className="text-[10px] text-[#1D1D1F]/40">{t.tag}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-xs text-[#1D1D1F]/40 font-medium mb-4">Test your idea before you invest.</p>
            <Button
              data-testid="testimonials-cta"
              onClick={handleStart}
              className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full py-3 px-8 text-sm font-semibold transition-all duration-500 active:scale-95"
            >
              Try it free
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-10 md:py-14" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs md:text-sm tracking-widest uppercase font-bold text-[#1D1D1F]/50 mb-3">
            How It Works
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#1D1D1F] mb-8">
            Four steps to clarity.
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Describe', desc: 'Enter your concept, cuisine, location, and AOV.' },
              { step: '02', title: 'Invest', desc: 'Add your rent, deposit, interiors, and capital.' },
              { step: '03', title: 'Operate', desc: 'Staff, food cost, packaging, and marketing.' },
              { step: '04', title: 'Revenue', desc: 'Daily orders, table turns, and revenue split.' },
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-[#1D1D1F]/15 text-5xl font-bold mb-4">{item.step}</div>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{item.title}</h3>
                <p className="text-sm text-[#1D1D1F]/60 leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Visual context */}
          <div className="mt-10 rounded-3xl overflow-hidden aspect-[21/8]">
            <img
              src="https://images.pexels.com/photos/29983408/pexels-photo-29983408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Premium restaurant interior"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="bg-[#F5F5F7] py-10 md:py-12" data-testid="models-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-6">
            <p className="text-xs md:text-sm tracking-widest uppercase font-bold text-[#1D1D1F]/50 mb-4">
              We Evaluate
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#1D1D1F]">
              Every food business model.
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 md:gap-4">
            {[
              { label: 'QSR', icon: 'https://images.pexels.com/photos/10636732/pexels-photo-10636732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
              { label: 'Cafe', icon: 'https://images.pexels.com/photos/35237767/pexels-photo-35237767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
              { label: 'Casual Dining', icon: 'https://images.pexels.com/photos/35056936/pexels-photo-35056936.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
              { label: 'Fine Dining', icon: 'https://images.unsplash.com/photo-1761095596849-608b6a337c36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxmaW5lJTIwZGluaW5nJTIwZWxlZ2FudCUyMHJlc3RhdXJhbnQlMjB0YWJsZSUyMHNldHRpbmd8ZW58MHx8fHwxNzc0ODYwODc0fDA&ixlib=rb-4.1.0&q=85' },
              { label: 'Bakery', icon: 'https://images.pexels.com/photos/30427448/pexels-photo-30427448.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
              { label: 'Hybrid', icon: 'https://images.pexels.com/photos/12603336/pexels-photo-12603336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
            ].map((item, i) => (
              <div key={i} className="text-center group">
                <div className="aspect-square rounded-2xl overflow-hidden mb-3 border border-[#E5E5EA] transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                  <img src={item.icon} alt={item.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <span className="text-xs font-semibold text-[#1D1D1F]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section className="bg-white py-10 md:py-14" data-testid="credibility-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
            <div>
              <p className="text-xs md:text-sm tracking-widest uppercase font-bold text-[#1D1D1F]/50 mb-4">
                Why Trust This Tool
              </p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#1D1D1F] mb-6">
                Built on real Indian F&B logic.
              </h2>
              <p className="text-base md:text-lg text-[#1D1D1F]/70 leading-relaxed mb-8 font-medium">
                This is not a generic AI tool. Every score, threshold, and penalty is derived from
                real-world restaurant economics specific to India. The scoring engine uses deterministic
                rules — AI only explains the output, never generates it.
              </p>
              <div className="space-y-4">
                {[
                  'Internal scoring engine — not AI-generated',
                  'Indian market benchmarks built in',
                  'Guided by industry expertise',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#1D1D1F] flex items-center justify-center">
                      <ChevronRight className="h-3 w-3 text-white" strokeWidth={2} />
                    </div>
                    <span className="text-sm font-semibold text-[#1D1D1F]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#F5F5F7] rounded-3xl p-8 md:p-10 border border-[#E5E5EA]">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-[#E5E5EA]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/dfc8hr56_MCI%202023%20-%20Ting%20011136%20copy%202.jpg"
                    alt="Rohit Chidurala"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1D1D1F] mb-1">Rohit Chidurala</h3>
                  <p className="text-sm text-[#1D1D1F]/60 mb-3 font-medium">
                    MasterChef India Telugu semifinalist, food entrepreneur, and consultant.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-[#1D1D1F] text-[#1D1D1F]" />
                      ))}
                    </div>
                    <a href="https://www.instagram.com/rohit_chidurala/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white text-[10px] font-semibold hover:opacity-90 transition-opacity">
                      <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                      @rohit_chidurala
                    </a>
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#1D1D1F]/70 mt-6 leading-relaxed font-medium">
                "I've seen hundreds of food businesses fail because founders didn't validate their
                numbers before investing. This tool forces that discipline."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="bg-[#F5F5F7] py-10 md:py-14" data-testid="methodology-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs md:text-sm tracking-widest uppercase font-bold text-[#1D1D1F]/50 mb-4">
              Methodology
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#1D1D1F] mb-6">
              Deterministic scoring. AI explains.
            </h2>
            <p className="text-base md:text-lg text-[#1D1D1F]/70 leading-relaxed mb-8 font-medium">
              All calculations — revenue, costs, breakeven, scoring — are computed using fixed
              formulas and threshold-based rules. AI (GPT/Gemini) is used only to generate a
              human-readable explanation of the structured output. It never changes scores.
            </p>
            <div className="grid grid-cols-3 gap-6 text-center">
              {[
                { value: '100', label: 'Point Scoring' },
                { value: '6', label: 'Score Categories' },
                { value: '7+', label: 'Risk Checks' },
              ].map((item, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-semibold text-[#1D1D1F]">{item.value}</div>
                  <div className="text-xs text-[#1D1D1F]/50 mt-1 font-semibold">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Share */}
      <section className="bg-white py-8 md:py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <ShareBar text="Know if your restaurant will work before you invest — Restaurant Feasibility Engine" />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1D1D1F] py-14 md:py-20" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-3 leading-tight">
            Don't let bad numbers<br />ruin a good idea.
          </h2>
          <p className="text-sm md:text-base text-white/50 leading-relaxed mb-8 max-w-md mx-auto font-medium">
            2 minutes. One honest score. Before you commit lakhs.
          </p>
          <Button
            data-testid="cta-start-btn"
            onClick={handleStart}
            className="bg-white text-[#1D1D1F] hover:bg-white/90 rounded-full py-4 px-10 text-base font-semibold transition-all duration-500 active:scale-95 shadow-[0_4px_20px_rgba(255,255,255,0.15)]"
            size="lg"
          >
            Check My Risk Now
            <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
